import { useState, useEffect, useRef } from 'react';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';

const GmailConfigAdmin = () => {
    const toast = useToast();
    const [status, setStatus] = useState(null);
    const [autoCheckStatus, setAutoCheckStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [pendingEmails, setPendingEmails] = useState([]);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [aiProvider, setAiProvider] = useState(null);
    const fileInputRef = useRef(null);

    // Load status on mount
    useEffect(() => {
        loadStatus();
        loadAutoCheckStatus();
        loadAiProvider();
    }, []);

    const loadStatus = async () => {
        try {
            setLoading(true);
            const data = await api.get('/gmail/status');
            setStatus(data);
        } catch (error) {
            toast.error('Error cargando estado de Gmail: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadAutoCheckStatus = async () => {
        try {
            const data = await api.get('/gmail/autocheck/status');
            setAutoCheckStatus(data);
        } catch (error) {
            console.error('Error loading autocheck status:', error);
        }
    };

    const handleToggleAutoCheck = async (enabled) => {
        try {
            await api.post('/gmail/autocheck/config', { enabled });
            toast.success(enabled ? 'Auto-check activado' : 'Auto-check desactivado');
            loadAutoCheckStatus();
        } catch (error) {
            toast.error('Error: ' + error.message);
        }
    };

    const handleChangeInterval = async (minutes) => {
        try {
            await api.post('/gmail/autocheck/config', { intervalMinutes: minutes });
            toast.success(`Intervalo actualizado a ${minutes} minutos`);
            loadAutoCheckStatus();
        } catch (error) {
            toast.error('Error: ' + error.message);
        }
    };

    const handleChangeActiveHours = async (start, end) => {
        try {
            await api.post('/gmail/autocheck/config', {
                activeHoursStart: start || '06:00',
                activeHoursEnd: end || '20:00'
            });
            toast.success('Horario activo actualizado');
            loadAutoCheckStatus();
        } catch (error) {
            toast.error('Error: ' + error.message);
        }
    };

    const handleChangeParseMode = async (parseMode) => {
        try {
            await api.post('/gmail/autocheck/config', { parseMode });
            const label = parseMode === 'text' ? 'Texto' : parseMode === 'both' ? 'Ambos' : 'Vision';
            toast.success(`Modo IA actualizado a ${label}`);
            loadAutoCheckStatus();
        } catch (error) {
            toast.error('Error: ' + error.message);
        }
    };

    const loadAiProvider = async () => {
        try {
            const data = await api.get('/gmail/ai-provider');
            setAiProvider(data);
        } catch (error) {
            console.error('Error loading AI provider:', error);
            toast.error('Error al cargar proveedor de IA: ' + error.message);
        }
    };

    const handleChangeAiProvider = async (provider) => {
        try {
            await api.post('/gmail/ai-provider', { provider });
            const label = provider === 'opencode' ? 'OpenCode Go (Qwen 3.8)' : provider === 'ollama' ? 'Ollama (Local)' : 'OpenAI (Cloud)';
            toast.success(`Proveedor de IA cambiado a ${label} correctamente.`);
            loadAiProvider();
        } catch (error) {
            toast.error('Error al cambiar proveedor de IA: ' + error.message);
        }
    };

    const handleCredentialsUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const text = await file.text();
            const credentials = JSON.parse(text);

            await api.post('/gmail/credentials', { credentials });
            toast.success('Credenciales guardadas correctamente');
            loadStatus();
        } catch (error) {
            toast.error('Error al procesar el archivo: ' + error.message);
        }
    };

    const handleStartAuth = async () => {
        try {
            const data = await api.get('/gmail/auth-url');
            if (data.url) {
                window.open(data.url, '_blank', 'width=600,height=700');
                toast.info('Completa la autorización en la ventana emergente');
            }
        } catch (error) {
            toast.error('Error: ' + error.message);
        }
    };

    const handleTestConnection = async () => {
        try {
            setTesting(true);
            const data = await api.post('/gmail/test-connection');
            toast.success(data.message);
            setPendingEmails(data.emails || []);
        } catch (error) {
            toast.error('Error de conexión: ' + error.message);
        } finally {
            setTesting(false);
        }
    };

    const handleRunImport = async () => {
        try {
            setImporting(true);
            const parseMode = autoCheckStatus?.parseMode || 'vision';
            const result = await api.post('/gmail/run-import', { parseMode });

            if (result.success) {
                toast.success(`Importación completada: ${result.pdfsProcessed} PDFs procesados (${parseMode})`);
                loadHistory();
            } else {
                toast.error(result.message || 'Error en la importación');
            }
        } catch (error) {
            toast.error('Error: ' + error.message);
        } finally {
            setImporting(false);
        }
    };

    const loadHistory = async () => {
        try {
            const data = await api.get('/gmail/history');
            setHistory(data.history || []);
        } catch (error) {
            console.error('Error loading history:', error);
        }
    };

    const handleLoadPending = async () => {
        try {
            const data = await api.get('/gmail/pending-emails');
            setPendingEmails(data.emails || []);
            toast.info(`${data.count} correos pendientes encontrados`);
        } catch (error) {
            toast.error('Error: ' + error.message);
        }
    };

    // Check for OAuth callback success
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('success') === 'true') {
            toast.success('¡Autorización completada con éxito!');
            loadStatus();
            // Clean URL
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.get('error')) {
            toast.error('Error en autorización: ' + params.get('error'));
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-[400px]">
                <div className="w-8 h-8 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 bg-clip-text text-transparent">
                    📧 Importación Automática de Emails
                </h1>
                <button
                    onClick={() => { loadStatus(); loadAutoCheckStatus(); }}
                    className="p-2 hover:bg-white/5 rounded-lg transition-colors text-xl"
                    title="Recargar estado"
                >
                    🔄
                </button>
            </div>

            {/* Status Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`card p-4 rounded-xl border ${status?.hasCredentials
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-red-500/10 border-red-500/30'}`}>
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">{status?.hasCredentials ? '✅' : '❌'}</span>
                        <div>
                            <p className="text-sm text-gray-400">Credenciales</p>
                            <p className="font-bold text-white">
                                {status?.hasCredentials ? 'Configuradas' : 'No configuradas'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className={`card p-4 rounded-xl border ${status?.hasTokens
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-amber-500/10 border-amber-500/30'}`}>
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">{status?.hasTokens ? '🔐' : '⏳'}</span>
                        <div>
                            <p className="text-sm text-gray-400">Autorización</p>
                            <p className="font-bold text-white">
                                {status?.hasTokens ? 'Activa' : 'Pendiente'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className={`card p-4 rounded-xl border ${status?.configured
                    ? 'bg-sky-500/10 border-sky-500/30'
                    : 'bg-gray-500/10 border-gray-500/30'}`}>
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">{status?.configured ? '☁️' : '💤'}</span>
                        <div>
                            <p className="text-sm text-gray-400">Sistema</p>
                            <p className="font-bold text-white">
                                {status?.configured ? 'Listo' : 'No listo'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Auto-Check Control Panel */}
            {status?.configured && (
                <div className="card p-6 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-2xl space-y-4">
                    {/* Header with toggle */}
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <span className="text-3xl">{autoCheckStatus?.enabled ? '🤖' : '😴'}</span>
                            <div>
                                <h2 className="text-lg font-bold text-white">
                                    Importación Automática
                                    {autoCheckStatus?.isRunning && (
                                        <span className="ml-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full font-medium">
                                            ACTIVO
                                        </span>
                                    )}
                                </h2>
                                <p className="text-sm text-gray-400">
                                    {autoCheckStatus?.enabled
                                        ? `Cada ${autoCheckStatus?.intervalMinutes} min · ${autoCheckStatus?.activeHoursStart || '06:00'} - ${autoCheckStatus?.activeHoursEnd || '20:00'}`
                                        : 'Desactivado'}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Modo IA: {autoCheckStatus?.parseMode === 'text' ? 'Texto' : autoCheckStatus?.parseMode === 'both' ? 'Ambos' : 'Vision'}
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={() => handleToggleAutoCheck(!autoCheckStatus?.enabled)}
                            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${autoCheckStatus?.enabled
                                ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30'
                                : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                                }`}
                        >
                            {autoCheckStatus?.enabled ? '⏹️ Detener' : '▶️ Activar'}
                        </button>
                    </div>

                    {/* Configuration row */}
                    <div className="flex flex-wrap items-center gap-4 p-4 bg-white/5 rounded-xl">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-400">Proveedor IA:</span>
                            <select
                                value={aiProvider?.provider || 'opencode'}
                                onChange={(e) => handleChangeAiProvider(e.target.value)}
                                className="px-3 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                            >
                                <option value="opencode" className="bg-slate-900 text-white">⚡ OpenCode Go (Qwen 3.8)</option>
                                <option value="ollama" className="bg-slate-900 text-white">🤖 Ollama (Local)</option>
                                <option value="openai" className="bg-slate-900 text-white">☁️ OpenAI (Cloud)</option>
                            </select>
                            {aiProvider?.provider === 'opencode' && (
                                <span className="text-xs text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded-full">
                                    Cuenta Go
                                </span>
                            )}
                            {aiProvider?.provider === 'ollama' && (
                                <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                                    Gratis
                                </span>
                            )}
                            {aiProvider?.provider === 'openai' && (
                                <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
                                    De pago
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-400">Modo IA:</span>
                            <select
                                value={autoCheckStatus?.parseMode || 'vision'}
                                onChange={(e) => handleChangeParseMode(e.target.value)}
                                className="px-3 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                            >
                                <option value="text" className="bg-slate-900 text-white">Texto</option>
                                <option value="vision" className="bg-slate-900 text-white">Vision</option>
                                <option value="both" className="bg-slate-900 text-white">Ambos</option>
                            </select>
                        </div>
                        {aiProvider?.provider === 'ollama' && autoCheckStatus?.parseMode !== 'vision' && (
                            <span className="text-xs text-amber-400">
                                ⚠️ Ollama solo soporta modo Vision
                            </span>
                        )}
                    </div>

                    {autoCheckStatus?.enabled && (
                        <div className="flex flex-wrap items-center gap-4 p-4 bg-white/5 rounded-xl">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-400">⏱️ Intervalo:</span>
                                <select
                                    value={autoCheckStatus?.intervalMinutes || 5}
                                    onChange={(e) => handleChangeInterval(Number(e.target.value))}
                                    className="px-3 py-1.5 bg-white/10 border border-white/10 rounded-lg text-white text-sm"
                                >
                                    <option value={1}>1 min</option>
                                    <option value={5}>5 min</option>
                                    <option value={10}>10 min</option>
                                    <option value={15}>15 min</option>
                                    <option value={30}>30 min</option>
                                    <option value={60}>60 min</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-400">🌅 Horario activo:</span>
                                <input
                                    type="time"
                                    value={autoCheckStatus?.activeHoursStart || '06:00'}
                                    onChange={(e) => handleChangeActiveHours(e.target.value, autoCheckStatus?.activeHoursEnd)}
                                    className="px-2 py-1.5 bg-white/10 border border-white/10 rounded-lg text-white text-sm"
                                />
                                <span className="text-gray-500">a</span>
                                <input
                                    type="time"
                                    value={autoCheckStatus?.activeHoursEnd || '20:00'}
                                    onChange={(e) => handleChangeActiveHours(autoCheckStatus?.activeHoursStart, e.target.value)}
                                    className="px-2 py-1.5 bg-white/10 border border-white/10 rounded-lg text-white text-sm"
                                />
                            </div>
                        </div>
                    )}

                    {/* Last Check Info */}
                    {autoCheckStatus?.lastCheck && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                            <span>🕐</span>
                            <span>Última revisión: {new Date(autoCheckStatus.lastCheck).toLocaleString('es-ES')}</span>
                            {autoCheckStatus.lastResult && (
                                <span className={`px-2 py-0.5 rounded text-xs ${autoCheckStatus.lastResult.success
                                    ? 'bg-emerald-500/20 text-emerald-400'
                                    : 'bg-red-500/20 text-red-400'
                                    }`}>
                                    {autoCheckStatus.lastResult.pdfsProcessed > 0
                                        ? `✅ ${autoCheckStatus.lastResult.pdfsProcessed} PDF(s)`
                                        : autoCheckStatus.lastResult.success
                                            ? '📭 Sin emails nuevos'
                                            : '❌ Error'}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Setup Steps */}
            <div className="card p-6 bg-white/5 border border-white/10 rounded-2xl space-y-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    ⚙️ Configuración
                </h2>

                {/* Step 1: Upload Credentials */}
                <div className={`p-4 rounded-xl border ${status?.hasCredentials
                    ? 'bg-emerald-500/5 border-emerald-500/20'
                    : 'bg-white/5 border-white/10'}`}>
                    <div className="flex items-start gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${status?.hasCredentials ? 'bg-emerald-500 text-white' : 'bg-sky-500/20 text-sky-400'
                            }`}>
                            {status?.hasCredentials ? '✓' : '1'}
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-white mb-1">Subir credenciales de Google Cloud</h3>
                            <p className="text-sm text-gray-400 mb-3">
                                Descarga el archivo JSON de credenciales OAuth desde Google Cloud Console y súbelo aquí.
                            </p>

                            <input
                                type="file"
                                accept=".json"
                                ref={fileInputRef}
                                onChange={handleCredentialsUpload}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="px-4 py-2 bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 rounded-lg text-sm transition-colors flex items-center gap-2"
                            >
                                📤 {status?.hasCredentials ? 'Reemplazar credenciales' : 'Subir archivo JSON'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Step 2: Authorize */}
                <div className={`p-4 rounded-xl border ${status?.hasTokens
                    ? 'bg-emerald-500/5 border-emerald-500/20'
                    : 'bg-white/5 border-white/10'} ${!status?.hasCredentials ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div className="flex items-start gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${status?.hasTokens ? 'bg-emerald-500 text-white' : 'bg-sky-500/20 text-sky-400'
                            }`}>
                            {status?.hasTokens ? '✓' : '2'}
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-white mb-1">Autorizar acceso a Gmail</h3>
                            <p className="text-sm text-gray-400 mb-3">
                                Inicia sesión con la cuenta de Gmail que recibe los correos de TUVISA y autoriza el acceso.
                            </p>

                            <button
                                onClick={handleStartAuth}
                                disabled={!status?.hasCredentials}
                                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors flex items-center gap-2"
                            >
                                🔑 {status?.hasTokens ? 'Re-autorizar' : 'Autorizar con Google'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Step 3: Test & Run */}
                <div className={`p-4 rounded-xl border ${status?.configured
                    ? 'bg-white/5 border-white/10'
                    : 'bg-white/5 border-white/10 opacity-50 pointer-events-none'}`}>
                    <div className="flex items-start gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${status?.configured ? 'bg-sky-500/20 text-sky-400' : 'bg-gray-500/20 text-gray-400'
                            }`}>
                            3
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-white mb-1">Probar conexión</h3>
                            <p className="text-sm text-gray-400 mb-3">
                                Verifica que la conexión funciona y que se detectan los correos de TUVISA.
                            </p>

                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={handleTestConnection}
                                    disabled={!status?.configured || testing}
                                    className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                                >
                                    {testing ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
                                            Probando...
                                        </>
                                    ) : (
                                        <>
                                            🔌 Probar conexión
                                        </>
                                    )}
                                </button>

                                <button
                                    onClick={handleLoadPending}
                                    disabled={!status?.configured}
                                    className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                                >
                                    📬 Ver pendientes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pending Emails */}
            <AnimatePresence>
                {pendingEmails.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="card p-6 bg-white/5 border border-white/10 rounded-2xl space-y-4"
                    >
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                📬 Correos pendientes ({pendingEmails.length})
                            </h2>
                            <button
                                onClick={handleRunImport}
                                disabled={importing}
                                className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-emerald-500/20"
                            >
                                {importing ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Importando...
                                    </>
                                ) : (
                                    <>
                                        ⬇️ Importar todos
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {pendingEmails.map((email, idx) => (
                                <div key={email.id || idx} className="p-3 bg-white/5 rounded-lg border border-white/5 flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-white">{email.subject}</p>
                                        <p className="text-xs text-gray-500">{email.date}</p>
                                    </div>
                                    <span className="text-amber-400">✉️</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Email Filter Info */}
            <div className="card p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
                <h3 className="font-bold text-blue-400 mb-2 flex items-center gap-2">
                    🔍 Filtro de correos configurado
                </h3>
                <div className="text-sm text-gray-300 space-y-1">
                    <p><span className="text-gray-500">Remitente:</span> <code className="bg-white/5 px-2 py-0.5 rounded">{status?.emailFilter?.sender}</code></p>
                    <p><span className="text-gray-500">Asunto contiene:</span> {
                        status?.emailFilter?.subjectPatterns
                            ? status.emailFilter.subjectPatterns.map((p, i) => (
                                <span key={i}>
                                    {i > 0 && <span className="text-gray-500 mx-1">ó</span>}
                                    <code className="bg-white/5 px-2 py-0.5 rounded">{p}</code>
                                </span>
                            ))
                            : <code className="bg-white/5 px-2 py-0.5 rounded">{status?.emailFilter?.subjectContains}</code>
                    }</p>
                </div>
            </div>

            {/* History Section */}
            <div className="card p-6 bg-white/5 border border-white/10 rounded-2xl">
                <button
                    onClick={() => {
                        setShowHistory(!showHistory);
                        if (!showHistory) loadHistory();
                    }}
                    className="w-full flex items-center justify-between text-left"
                >
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        📃 Historial de importaciones
                    </h2>
                    <span className={`text-gray-400 transition-transform ${showHistory ? 'rotate-180' : ''}`}>
                        ▼
                    </span>
                </button>

                <AnimatePresence>
                    {showHistory && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-4 space-y-2 overflow-hidden"
                        >
                            {history.length === 0 ? (
                                <p className="text-gray-500 text-sm py-4 text-center">No hay importaciones registradas</p>
                            ) : (
                                history.slice(0, 20).map((item, idx) => (
                                    <div key={idx} className={`p-3 rounded-lg border ${item.success
                                        ? 'bg-emerald-500/5 border-emerald-500/20'
                                        : 'bg-red-500/5 border-red-500/20'
                                        }`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-white">{item.filename}</p>
                                                <p className="text-xs text-gray-500">
                                                    {item.date} • {item.totalHistorico || 0} registros
                                                </p>
                                            </div>
                                            <span>{item.success ? '✅' : '❌'}</span>
                                        </div>
                                        {item.error && (
                                            <p className="text-xs text-red-400 mt-1">{item.error}</p>
                                        )}
                                    </div>
                                ))
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Instructions */}
            <div className="card p-6 bg-gradient-to-br from-purple-500/5 to-sky-500/5 border border-purple-500/20 rounded-2xl">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    ❓ Instrucciones de configuración
                </h2>

                <ol className="space-y-3 text-sm text-gray-300">
                    <li className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                        <span>Ve a <a href="https://console.cloud.google.com" target="_blank" rel="noopener" className="text-purple-400 underline">Google Cloud Console</a> y selecciona tu proyecto</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                        <span>Ve a <strong>APIs & Services → Credentials</strong></span>
                    </li>
                    <li className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                        <span>Crea credenciales <strong>OAuth 2.0 Client ID</strong> tipo "Web application"</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold shrink-0">4</span>
                        <div>
                            <span>Añade estos <strong>Authorized redirect URIs</strong>:</span>
                            <ul className="mt-1 ml-4 space-y-1 text-xs">
                                <li><code className="bg-white/5 px-2 py-0.5 rounded">http://localhost:3000/api/gmail/callback</code></li>
                                <li><code className="bg-white/5 px-2 py-0.5 rounded">https://calendario.xerp.es/api/gmail/callback</code></li>
                            </ul>
                        </div>
                    </li>
                    <li className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold shrink-0">5</span>
                        <span>Descarga el JSON y súbelo en el paso 1</span>
                    </li>
                </ol>
            </div>
        </div>
    );
};

export default GmailConfigAdmin;
